/**
 * utils/feeFormTransforms.ts
 * Utility functions for transforming fee form data to BrokerFeeConfiguration
 * 
 * Handles conversion between modal form structures and the persistent
 * BrokerFeeConfiguration format used in OnboardingDraft.
 */

import type { BrokerFeeConfiguration } from '@/types/reference/BrokerFeeCatalog';
import type { CommissionStructure } from '@/types/shared/feeStructures/CommissionStructure';
import type { AssetClass, AssetClassType, MarketSegment, SubAssetClass } from '@/types/shared/AssetClass';

/**
 * Form data structure from FeeAssignmentModal
 * (Re-exported here to avoid circular dependencies)
 */
export interface FeeFormData {
  hasCommission: boolean;
  commissionType: 'fixed' | 'percentage' | 'perContract' | 'tiered';
  commissionValue: string;
  commissionCurrency: string;
  commissionStructure: 'perSide' | 'roundTrip';
  commissionMinimum?: string;
  
  hasOvernightFinancing: boolean;
  overnightBenchmark: string;
  overnightSpread: string;
  overnightFrequency: 'daily' | 'weekends' | 'holidays';
  
  hasCurrencyConversion: boolean;
  conversionRateType: 'spot' | 'interbank' | 'spotPlusMarkup';
  conversionMarkup: string;
  
  notes: string;
}

/**
 * Configuration for fee assignment context
 */
export interface FeeAssignmentContext {
  /** Asset class for this fee configuration */
  assetClass: AssetClassType;
  /** Optional market segment */
  marketSegment?: MarketSegment | undefined;
  /** Optional sub-asset class */
  subAssetClass?: SubAssetClass | undefined;
  /** Optional instrument ID for instrument-specific fees */
  instrumentId?: string | undefined;
  /** Display name for the configuration */
  name: string;
}

/**
 * Convert form commission type to CommissionStructure type
 */
const mapCommissionType = (formType: FeeFormData['commissionType']): CommissionStructure['feeType'] => {
  switch (formType) {
    case 'fixed':
      return 'fixedPerTransaction';
    case 'percentage':
      return 'percentage';
    case 'perContract':
      return 'fixedPerUnit';
    case 'tiered':
      return 'tiered';
    default:
      return 'fixedPerUnit';
  }
};

/**
 * Convert form commission structure to CommissionStructure timing
 */
const mapCommissionTiming = (formStructure: FeeFormData['commissionStructure']): CommissionStructure['chargeOn'] => {
  switch (formStructure) {
    case 'perSide':
      return 'perSide';
    case 'roundTrip':
      return 'roundTrip';
    default:
      return 'perSide';
  }
};

/**
 * Convert string amount to integer representation with precision
 */
const parseMonetaryValue = (value: string, defaultPrecision: number = 2): { amount: number; precision: number } => {
  if (!value || value.trim() === '') {
    return { amount: 0, precision: defaultPrecision };
  }
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return { amount: 0, precision: defaultPrecision };
  }
  
  // Convert to integer representation (e.g., $1.50 becomes 150 with precision 2)
  const precision = defaultPrecision;
  const amount = Math.round(numValue * Math.pow(10, precision));
  
  return { amount, precision };
};

/**
 * Transform FeeFormData to BrokerFeeConfiguration
 */
export const transformFormToBrokerFeeConfig = (
  formData: FeeFormData,
  context: FeeAssignmentContext,
  configId?: string
): BrokerFeeConfiguration => {
  // Build commission structure
  const commission: CommissionStructure = {
    feeType: mapCommissionType(formData.commissionType),
    chargeOn: mapCommissionTiming(formData.commissionStructure),
    verificationStatus: 'userOverride' // union literal; do not replace with enum
  };
  
  // Add fee amount if commission is enabled
  if (formData.hasCommission && formData.commissionValue) {
    const { amount, precision } = parseMonetaryValue(formData.commissionValue);
    commission.feeAmount = amount;
    commission.feePrecision = precision;
    
    // Add minimum if specified
    if (formData.commissionMinimum) {
      const { amount: minAmount, precision: minPrecision } = parseMonetaryValue(formData.commissionMinimum);
      commission.minPerOrder = minAmount;
      commission.minPerOrderPrecision = minPrecision;
    }
  }
  
  // Build asset class category
  const category: AssetClass = {
    assetClass: context.assetClass,
    // For asset class level assignments, use empty string (matches addAssetClass logic in useFeeHierarchy)
    // This ensures fees are assigned to the asset class level, not creating an 'other' market segment
    marketSegment: context.marketSegment || ('' as MarketSegment)
  };

  if (context.subAssetClass) {
    category.subAssetClass = context.subAssetClass;
  }
  
  // Build base configuration
  const config: BrokerFeeConfiguration = {
    id: configId || `fee-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name: context.name,
    category,
    commission
  };
  
  // Add notes if present
  if (formData.notes && formData.notes.trim() !== '') {
    config.notes = formData.notes;
  }
  
  // Add instrument ID if this is instrument-specific
  if (context.instrumentId) {
    config.instrumentId = context.instrumentId;
  }
  
  // Add overnight financing if enabled
  if (formData.hasOvernightFinancing && formData.overnightSpread) {
    const { amount: spreadAmount, precision: spreadPrecision } = parseMonetaryValue(formData.overnightSpread, 4); // basis points
    
    config.overnightFinancing = {
      benchmark: formData.overnightBenchmark,
      adminSpread: spreadAmount,
      adminSpreadPrecision: spreadPrecision,
      frequency: formData.overnightFrequency === 'daily' ? 'daily' : 'weekends',
      verificationStatus: 'userOverride' // union literal; do not replace with enum
    };
  }
  
  // Add currency conversion if enabled
  if (formData.hasCurrencyConversion && formData.conversionMarkup) {
    const { amount: markupAmount, precision: markupPrecision } = parseMonetaryValue(formData.conversionMarkup, 4); // basis points
    
    config.currencyConversion = {
      rateType: formData.conversionRateType === 'spot' ? 'spot' :
                formData.conversionRateType === 'interbank' ? 'interbank' : 'spotPlusMarkup',
      markup: markupAmount,
      markupPrecision: markupPrecision,
      usesRealTimeRates: true,
      verificationStatus: 'userOverride' // union literal; do not replace with enum
    };
  }
  
  return config;
};

/**
 * Transform BrokerFeeConfiguration back to FeeFormData for editing
 */
export const transformBrokerFeeConfigToForm = (config: BrokerFeeConfiguration): FeeFormData => {
  // Convert commission structure
  const commission = config.commission;
  let commissionType: FeeFormData['commissionType'] = 'fixed';

  // Reverse transform: map CommissionType union literals to form types
  switch (commission.feeType) {
    case 'fixedPerTransaction':
      commissionType = 'fixed';
      break;
    case 'percentage':
      commissionType = 'percentage';
      break;
    case 'fixedPerUnit':
      commissionType = 'perContract';
      break;
    case 'tiered':
      commissionType = 'tiered';
      break;
  }
  
  const commissionStructure: FeeFormData['commissionStructure'] =
    commission.chargeOn === 'roundTrip' ? 'roundTrip' : 'perSide';
  
  // Convert monetary values back to strings
  const commissionValue = commission.feeAmount && commission.feePrecision !== undefined
    ? (commission.feeAmount / Math.pow(10, commission.feePrecision)).toString()
    : '';
  
  const commissionMinimum = commission.minPerOrder && commission.minPerOrderPrecision !== undefined
    ? (commission.minPerOrder / Math.pow(10, commission.minPerOrderPrecision)).toString()
    : '';
  
  // Convert overnight financing
  const hasOvernightFinancing = !!config.overnightFinancing;
  const overnightSpread = config.overnightFinancing?.adminSpread && config.overnightFinancing?.adminSpreadPrecision !== undefined
    ? (config.overnightFinancing.adminSpread / Math.pow(10, config.overnightFinancing.adminSpreadPrecision)).toString()
    : '';
  
  // Convert currency conversion
  const hasCurrencyConversion = !!config.currencyConversion;
  const conversionMarkup = config.currencyConversion?.markup && config.currencyConversion?.markupPrecision !== undefined
    ? (config.currencyConversion.markup / Math.pow(10, config.currencyConversion.markupPrecision)).toString()
    : '';
  
  let conversionRateType: FeeFormData['conversionRateType'] = 'spotPlusMarkup';
  if (config.currencyConversion?.rateType === 'spot') {
    conversionRateType = 'spot';
  } else if (config.currencyConversion?.rateType === 'interbank') {
    conversionRateType = 'interbank';
  }
  
  return {
    hasCommission: !!commission.feeAmount,
    commissionType,
    commissionValue,
    commissionCurrency: 'USD', // Default - could be extracted from config if stored
    commissionStructure,
    commissionMinimum,
    
    hasOvernightFinancing,
    overnightBenchmark: config.overnightFinancing?.benchmark || 'SOFR',
    overnightSpread,
    overnightFrequency: config.overnightFinancing?.frequency === 'daily' ? 'daily' : 'weekends',
    
    hasCurrencyConversion,
    conversionRateType,
    conversionMarkup,
    
    notes: config.notes || ''
  };
};